import React, { useEffect } from "react";
import styled from "@emotion/styled";
import {
  JOHN_DEERE_COMBINES_CONFIG,
  useDevicesScreenVM,
} from "@screens/DevicesScreen/DevicesScreenVM";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import Layout from "@components/Layout";
import CreateDeviceButton from "@screens/DevicesScreen/CreateDeviceButton";
import ImportFromJohnDeereButton from "@screens/DevicesScreen/ImportFromJohnDeereButton";
import { Row } from "@components/Flex";
import Spinner from "@components/Spinner";
import DevicesListHeader from "@screens/DevicesScreen/DevicesListHeader";
import DevicesList from "@screens/DevicesScreen/DevicesList";
import CreateDeviceDialog from "@screens/DevicesScreen/dialogs/CreateDeviceDialog";
import ImportJohnDeereMachinesDialog from "@screens/DevicesScreen/dialogs/ImportJohnDeereMachinesDialog";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

// One line on desktop, New device first. flex-shrink: 0 because the header's
// title is a full-width flex sibling: a shrinkable group with wrapping on gets
// squeezed down to one button's width and the buttons stack. Wrapping is only
// allowed on a phone, where the two buttons cannot fit beside the title.
const Actions = styled(Row)`
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap;
  }
`;

const DevicesScreenImpl: React.FC = () => {
  const vm = useDevicesScreenVM();
  const lang = useLang();
  const { settingsStore } = useStores();
  // const subtitle = vm.initialized
  //   ? vm.devices.length > 0
  //     ? lang.devices.devicesTagsSubtitle
  //     : lang.devices.devicesInitialSubtitle
  //   : "";
  const isNoDevices = vm.devices.length === 0;

  // Configs load after sign-in, so probe John Deere once the flag resolves —
  // never for users without it (the button is theirs only).
  const jdEnabled = settingsStore.hasConfig(JOHN_DEERE_COMBINES_CONFIG);
  useEffect(() => {
    if (jdEnabled) vm.checkJohnDeere();
  }, [jdEnabled, vm]);
  const showJdImport = jdEnabled && vm.johnDeereConnected;

  const handleCloseCreateDialog = () => {
    vm.setEditedDevice(null);
    vm.setCreateDialogOpened(false);
  };

  // Right-aligned in the header, left-aligned in the empty state (where the
  // lone button was).
  const actions = (justifyContent: "flex-end" | "start") => (
    <Actions alignItems="center" justifyContent={justifyContent} mainAxisSize="fit-content">
      <CreateDeviceButton />
      {showJdImport && <ImportFromJohnDeereButton />}
    </Actions>
  );

  return (
    <Layout
      title={lang.devices.devicesTitle}
      // subtitle={subtitle}
      extra={vm.initialized && !isNoDevices ? actions("flex-end") : null}
    >
      {!vm.initialized && (
        <Row alignItems="center" justifyContent="center" crossAxisSize="max">
          <Spinner />
        </Row>
      )}
      {vm.initialized && !isNoDevices && (
        <>
          <DevicesListHeader />
          <DevicesList />
        </>
      )}
      {vm.initialized && isNoDevices && actions("start")}
      <CreateDeviceDialog
        onSubmit={vm.editedDevice != null ? vm.edit : vm.create}
        visible={vm.createDialogOpened}
        onClose={handleCloseCreateDialog}
      />
      {jdEnabled && (
        <ImportJohnDeereMachinesDialog
          visible={vm.importJohnDeereOpened}
          onClose={() => vm.setImportJohnDeereOpened(false)}
          onSuccess={() => {
            vm.sync();
            vm.syncActivity();
          }}
        />
      )}
    </Layout>
  );
};

export default observer(DevicesScreenImpl);
